/* eslint-disable no-unused-vars */
import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import ProposalOrderHeader from "./ProposalOrderHeader";
import ProposalOrderClientCard from "./ProposalOrderClientCard";
import ProposalOrderWineList from "./ProposalOrderWineList";
import ProposalOrderForm from "./ProposalOrderForm";
import ProposalOrderPaymentMethod from "./ProposalOrderPaymentMethod";

const OrdersProposal = ({ hasPermission, proposalId: propId }) => {
    const { slug2 } = useParams();
    const navigate = useNavigate();

    const activeProposalId = propId || slug2;
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    const canEdit = Boolean(hasPermission?.("Proposals", "edit")) || !hasPermission;

    // Loading states
    const [isLoading, setIsLoading] = useState(true);

    // Proposal Data
    const [proposal, setProposal] = useState(null);
    const [wines, setWines] = useState([]);

    // Provinces Data
    const [provincesList, setProvincesList] = useState([]);

    // Form Validation Errors
    const [formErrors, setFormErrors] = useState({});

    // Payment Condition Default
    const [defaultPaymentCondition, setDefaultPaymentCondition] = useState("");

    // Address Mode: 'existing' | 'new'
    const [addressMode, setAddressMode] = useState("existing");
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState("primary");
    const [savedClientAddress, setSavedClientAddress] = useState(null);

    // Order Form Data
    const [formData, setFormData] = useState({
        order_date: dayjs().format("YYYY-MM-DD"),
        delivery_date: "",
        reference: "",
        company_name_recipient: "",
        address_title: "",
        destination_address: "",
        destination_city: "",
        destination_postal_code: "",
        destination_province: "",
        destination_country: "Italia",
        destination_phone: "",
        destination_mobile_phone: "",
        destination_fax: "",
        destination_email: "",
        delivery_notes: "",
        notes: "",
        payment_conditions_code: "",
    });


    // 1. Fetch Proposal Details
    const fetchProposal = useCallback(async () => {
        if (!activeProposalId) return;
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${apiUrl}/api/v1/proposals/${activeProposalId}/order-preview`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response?.data?.success) {
                const data = response.data?.data || {};

                // Map proposal & restaurant from order-preview structure
                const proposalInfo = data.proposal || {};
                const restaurantInfo = data.restaurant || {};
                const mergedProposal = {
                    ...data,
                    ...proposalInfo,
                    restaurant: restaurantInfo,
                };

                setProposal(mergedProposal);
                setWines(data?.wines || []);

                // Pre-populate form data matching order-preview
                const target = restaurantInfo;
                const resolvedDeliveryNotes = target.delivery_notes || "";
                const resolvedCompanyName = proposalInfo.display_client_name || target.company_name || "";
                const resolvedPaymentCondition = data?.default_payment_method?.code || "";

                setDefaultPaymentCondition(resolvedPaymentCondition);

                // Delivery addresses directly from API (no extra mapping)
                const deliveryAddresses = Array.isArray(data.delivery_addresses)
                    ? data.delivery_addresses
                    : [];

                const defaultAddress =
                    deliveryAddresses.find((a) => a?.is_legal_default) ||
                    deliveryAddresses[0] ||
                    null;

                setSavedAddresses(deliveryAddresses);
                setSelectedAddressId(defaultAddress?.id ?? 0);
                setSavedClientAddress(defaultAddress);
                setAddressMode("existing");

                setFormData((prev) => ({
                    ...prev,
                    reference: `PROP-${proposalInfo.id || activeProposalId}`,
                    delivery_notes: resolvedDeliveryNotes,
                    notes: `Ordine generato a partire dalla proposta #${proposalInfo.id || activeProposalId}`,
                    payment_conditions_code: resolvedPaymentCondition,
                    company_name_recipient: resolvedCompanyName,
                    address_title: defaultAddress?.address_title || "",
                    destination_address: defaultAddress?.address || "",
                    destination_city: defaultAddress?.city || "",
                    destination_postal_code: defaultAddress?.postal_code || "",
                    destination_province: defaultAddress?.province || "",
                    destination_phone: defaultAddress?.telephone || "",
                    destination_mobile_phone: defaultAddress?.mobile_no || "",
                    destination_fax: defaultAddress?.fax || "",
                    destination_email: defaultAddress?.email || "",
                }));
            } else {
                console.error(
                    "Error loading proposal preview:",
                    response?.data?.message || "Errore nel caricamento dei dati della proposta."
                );
            }
        } catch (error) {
            console.error("Error fetching proposal details:", error);
            toast.error("Errore nel caricamento dei dati della proposta.", { theme: "colored" });
        } finally {
            setIsLoading(false);
        }
    }, [activeProposalId, apiUrl, token]);

    // 2. Fetch Provinces List
    const fetchProvinces = useCallback(async () => {
        try {
            const response = await axios.get(`${apiUrl}/api/v1/provinces`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setProvincesList(response?.data?.data || response?.data || []);
        } catch (error) {
            console.error("Error fetching provinces:", error);
        }
    }, [apiUrl, token]);

    useEffect(() => {
        fetchProposal();
        fetchProvinces();
    }, [fetchProposal, fetchProvinces]);

    // Handle address mode switch: 'existing' vs 'new'
    const handleAddressModeChange = (nextMode) => {
        if (nextMode === addressMode) return;

        // Reset errors when switching mode
        setFormErrors({});

        if (nextMode === "new") {
            // Switch to 'new' mode: Reset all form fields to completely empty
            setAddressMode("new");
            setFormData((prev) => ({
                ...prev,
                company_name_recipient: "",
                address_title: "",
                destination_address: "",
                destination_city: "",
                destination_postal_code: "",
                destination_province: "",
                destination_country: "Italia",
                destination_phone: "",
                destination_mobile_phone: "",
                destination_fax: "",
                destination_email: "",
            }));
        } else {
            // Switching back to 'existing'
            setAddressMode("existing");

            // Restore saved client address
            const targetAddr =
                savedClientAddress ||
                (Array.isArray(savedAddresses)
                    ? savedAddresses.find(
                          (a, idx) =>
                              String(a?.id ?? idx) === String(selectedAddressId ?? 0)
                      ) || savedAddresses[0]
                    : null);

            if (targetAddr) {
                setFormData((prev) => ({
                    ...prev,
                    address_title: targetAddr.address_title || "",
                    destination_address: targetAddr.address || "",
                    destination_city: targetAddr.city || "",
                    destination_postal_code: targetAddr.postal_code || "",
                    destination_province: targetAddr.province || "",
                    destination_phone: targetAddr.telephone || "",
                    destination_mobile_phone: targetAddr.mobile_no || "",
                    destination_fax: targetAddr.fax || "",
                    destination_email: targetAddr.email || "",
                }));
            }
        }
    };

    // Handle form inputs change
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear error as user types into this field
        if (formErrors[name]) {
            setFormErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    // Handle selection of a specific saved address card
    const handleSelectSavedAddress = (addr, idx) => {
        setFormErrors({});
        setSelectedAddressId(addr?.id ?? idx ?? 0);
        setSavedClientAddress(addr);
        setFormData((prev) => ({
            ...prev,
            address_title: addr?.address_title || "",
            destination_address: addr?.address || "",
            destination_city: addr?.city || "",
            destination_postal_code: addr?.postal_code || "",
            destination_province: addr?.province || "",
            destination_phone: addr?.telephone || "",
            destination_mobile_phone: addr?.mobile_no || "",
            destination_fax: addr?.fax || "",
            destination_email: addr?.email || "",
        }));
    };

    // Handle Save: Address Add / Update only (Commented out)
    const [isSaving, setIsSaving] = useState(false);
    const [validationTrigger, setValidationTrigger] = useState(0);

    /*
    const handleSaveAddress = async () => {
        if (!canEdit) {
            toast.error("Non hai i permessi per modificare l'indirizzo.", { theme: "colored" });
            return;
        }

        // Validate mandatory address fields with proper field-specific messages
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.address_title || !String(formData.address_title).trim()) {
            errors.address_title = "Il titolo dell'indirizzo è obbligatorio";
        }

        if (!formData.destination_address || !String(formData.destination_address).trim()) {
            errors.destination_address = "L'indirizzo è obbligatorio";
        }

        if (!formData.destination_city || !String(formData.destination_city).trim()) {
            errors.destination_city = "La città è obbligatoria";
        }

        if (!formData.destination_postal_code || !String(formData.destination_postal_code).trim()) {
            errors.destination_postal_code = "Il CAP è obbligatorio";
        }

        if (!formData.destination_province || !String(formData.destination_province).trim()) {
            errors.destination_province = "La provincia è obbligatoria";
        }

        if (!formData.destination_phone || !String(formData.destination_phone).trim()) {
            errors.destination_phone = "Il numero di telefono è obbligatorio";
        }

        if (!formData.destination_mobile_phone || !String(formData.destination_mobile_phone).trim()) {
            errors.destination_mobile_phone = "Il numero di cellulare è obbligatorio";
        }

        const emailVal = String(formData.destination_email || "").trim();
        if (!emailVal) {
            errors.destination_email = "L'indirizzo email è obbligatorio";
        } else if (!emailRegex.test(emailVal)) {
            errors.destination_email = "Inserisci un indirizzo email valido";
        }

        if (!formData.destination_fax || !String(formData.destination_fax).trim()) {
            errors.destination_fax = "Il numero di fax è obbligatorio";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setValidationTrigger((prev) => prev + 1);
            return;
        }

        setFormErrors({});
        setIsSaving(true);

        const currentAddressItem = {
            address: formData.destination_address,
            city: formData.destination_city,
            postal_code: formData.destination_postal_code,
            cap: formData.destination_postal_code,
            province: formData.destination_province,
            country: formData.destination_country || "Italia",
            telephone: formData.destination_phone,
            mobile_phone: formData.destination_mobile_phone,
            mobile_no: formData.destination_mobile_phone,
            fax: formData.destination_fax,
            email: formData.destination_email,
            // Keep destination prefixed properties for seamless component compatibility
            destination_address: formData.destination_address,
            destination_city: formData.destination_city,
            destination_postal_code: formData.destination_postal_code,
            destination_province: formData.destination_province,
            destination_phone: formData.destination_phone,
            destination_mobile_phone: formData.destination_mobile_phone,
            destination_fax: formData.destination_fax,
            destination_email: formData.destination_email,
        };

        let addressesArray = [];

        if (addressMode === "new") {
            // Mode: ADD NEW ADDRESS -> Append new address into array
            const newAddressId = `addr-new-${Date.now()}`;
            const newAddressEntry = {
                id: newAddressId,
                label: `Sede ${savedAddresses.length + 1}`,
                is_default: savedAddresses.length === 0,
                ...currentAddressItem,
            };

            addressesArray = [
                ...savedAddresses.map((addr) => ({
                    id: addr.id,
                    label: addr.label,
                    is_default: Boolean(addr.is_default),
                    address: addr.destination_address || addr.address,
                    city: addr.destination_city || addr.city,
                    postal_code: addr.destination_postal_code || addr.postal_code || addr.cap,
                    cap: addr.destination_postal_code || addr.postal_code || addr.cap,
                    province: addr.destination_province || addr.province,
                    country: addr.destination_country || addr.country || "Italia",
                    telephone: addr.destination_phone || addr.telephone,
                    mobile_phone: addr.destination_mobile_phone || addr.mobile_phone || addr.mobile_no,
                    mobile_no: addr.destination_mobile_phone || addr.mobile_phone || addr.mobile_no,
                    fax: addr.destination_fax || addr.fax,
                    email: addr.destination_email || addr.email,
                })),
                newAddressEntry,
            ];

            // Update local state to reflect newly added address card
            setSavedAddresses((prev) => [...prev, newAddressEntry]);
            setSelectedAddressId(newAddressId);
            setSavedClientAddress(newAddressEntry);
            setAddressMode("existing");
        } else {
            // Mode: UPDATE EXISTING ADDRESS -> Update the selected address item in array
            addressesArray = savedAddresses.map((addr) => {
                const isSelected = String(addr.id) === String(selectedAddressId);
                if (isSelected) {
                    return {
                        id: addr.id,
                        label: addr.label,
                        is_default: Boolean(addr.is_default),
                        ...currentAddressItem,
                    };
                }
                return {
                    id: addr.id,
                    label: addr.label,
                    is_default: Boolean(addr.is_default),
                    address: addr.destination_address || addr.address,
                    city: addr.destination_city || addr.city,
                    postal_code: addr.destination_postal_code || addr.postal_code || addr.cap,
                    cap: addr.destination_postal_code || addr.postal_code || addr.cap,
                    province: addr.destination_province || addr.province,
                    country: addr.destination_country || addr.country || "Italia",
                    telephone: addr.destination_phone || addr.telephone,
                    mobile_phone: addr.destination_mobile_phone || addr.mobile_phone || addr.mobile_no,
                    mobile_no: addr.destination_mobile_phone || addr.mobile_phone || addr.mobile_no,
                    fax: addr.destination_fax || addr.fax,
                    email: addr.destination_email || addr.email,
                };
            });

            // Update local state for the modified address
            setSavedAddresses((prev) =>
                prev.map((addr) =>
                    String(addr.id) === String(selectedAddressId)
                        ? { ...addr, ...currentAddressItem }
                        : addr
                )
            );
        }

        const payload = {
            restaurant_id: proposal?.restaurant_id || proposal?.restaurant?.id,
            proposal_id: proposal?.id || activeProposalId,
            client_type: proposal?.client_type,
            action: addressMode === "new" ? "add_address" : "update_address",
            address_mode: addressMode,
            selected_address_id: selectedAddressId,
            addresses: addressesArray,
            current_address: currentAddressItem,
        };

        toast.success(
            addressMode === "new"
                ? "Nuovo indirizzo aggiunto con successo!"
                : "Indirizzo aggiornato con successo!",
            { theme: "colored" }
        );

        setIsSaving(false);
    };
    */

    return (
        <div className="w-full pb-10">
            {/* Header */}
            <ProposalOrderHeader
                proposalId={proposal?.id || activeProposalId}
                // onSave={handleSaveAddress}
                // isSaving={isSaving}
                canEdit={canEdit}
            />

            {/* Section 1: Order Overview (Client & Proposal Summary Card) */}
            <ProposalOrderClientCard
                proposal={proposal}
                isLoading={isLoading}
            />

            {/* Section 2: Proposal Order Wine List */}
            <ProposalOrderWineList
                wines={wines}
                financialSummary={proposal?.financial_summary}
                isLoading={isLoading}
            />

            {/* Section 3: Delivery & Order Details Form (Destinazione Merce) */}
            <ProposalOrderForm
                formData={formData}
                onFormChange={handleFormChange}
                provincesList={provincesList}
                addressMode={addressMode}
                onAddressModeChange={handleAddressModeChange}
                savedAddresses={savedAddresses}
                selectedAddressId={selectedAddressId}
                onSelectSavedAddress={handleSelectSavedAddress}
                errors={formErrors}
                validationTrigger={validationTrigger}
            />

            {/* Section 4: Payment Method (Metodo di Pagamento) */}
            <ProposalOrderPaymentMethod
                payment_conditions_code={formData.payment_conditions_code}
                onChange={handleFormChange}
                defaultPaymentCondition={defaultPaymentCondition}
                errors={formErrors}
            />
        </div>
    );
};

OrdersProposal.propTypes = {
    hasPermission: PropTypes.func,
    proposalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default OrdersProposal;

